import React, {useContext, useEffect, useMemo, useState} from "react";
import {StoreContext} from "../context/StoreContext";
import axios from "axios";
import {TbShoppingBagDiscount} from "react-icons/tb";
import {FaCar} from "react-icons/fa";
import {FaBus} from "react-icons/fa";
import {useLocation, useNavigate} from "react-router-dom";
import {FaMinus, FaPlus} from "react-icons/fa6";
import {IoTimeOutline} from "react-icons/io5";
import OfferBenefitCard from "../components/OfferBenefitCard";
import {TailSpin} from 'react-loader-spinner';
import {formatHourLabel, getClosingSoonMessage} from "../utils/restaurantHours";


function Checkout() {
  const [loading, setLoading] = useState(true);
  const {
    cartData,
    deviceId,
    userId,
    prevRestaurantId,
    accessToken,
    orderId,
    setOrderId,
    setCartData,
    offerData,
    setOfferData,
    showCheckout,
    setShowCheckout,
    paymentBreakup,
    setPaymentBreakup,
    source,
    roadId: positionGeohash,
    tableId,
    qrId,
    useDefaultTimeToArrive,
  } = useContext(StoreContext);

  
  const navigate = useNavigate();
  const location = useLocation();

  // Restaurant hours are only known if we arrived here with router state
  // (e.g. straight from the menu page) that carries opensAt/closesAt.
  const restaurantHours = location.state || {};
  const closingSoonLabel = getClosingSoonMessage({
    opensAt: restaurantHours.opensAt,
    closesAt: restaurantHours.closesAt,
  });

  // current location
  const [lat, lon] = source ? source.split(",").map(Number) : [0, 0];

  // checkout card data
  const [checkoutItems, setCheckoutItems] = useState([]);
  const [timeOfDelivery, setTimeOfDelivery] = useState(0);

  const [address, setAddress] = useState();
  const [restaurantName, setRestaurantName] = useState();

  // offer and benefit
  const [offerDetails, setOfferDetails] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);

  // New state for dine-in and takeaway selection
  const [orderType, setOrderType] = useState();
  const [payAtRestaurant, setPayAtRestaurant] = useState(false);
  const [showBusDetailsModal, setShowBusDetailsModal] = useState(false);
  const [busNumber, setBusNumber] = useState("");
  const [seatNumber, setSeatNumber] = useState("");
  const [busDetailsError, setBusDetailsError] = useState("");

  // Forced ETA entry when checkout couldn't return one (e.g. PREORDER_WITHOUT_LOCATION)
  const ETA_DEFAULT_MINUTES = 10;
  // When a predecessor page has opted in (e.g. bus tracking, where the bus's
  // own arrival decides the ETA), checkout skips the prompt and uses this.
  const DEFAULT_TIME_TO_ARRIVE_MINUTES = 24 * 60;
  const [showEtaModal, setShowEtaModal] = useState(false);
  const [etaMinutes, setEtaMinutes] = useState("");
  const [etaError, setEtaError] = useState("");
  const [submittingEta, setSubmittingEta] = useState(false);
  const [etaOverrideSubmitted, setEtaOverrideSubmitted] = useState(false);
  // Tracks whether the user actually adjusted the value before confirming —
  // if not, the first confirm tap just shakes the dialog as a nudge to double-check.
  const [etaAdjusted, setEtaAdjusted] = useState(false);
  const [etaConfirmWarned, setEtaConfirmWarned] = useState(false);
  const [shakeEtaDialog, setShakeEtaDialog] = useState(false);

  const [error, setError] = useState(null);
  const activeRestaurantId =
    prevRestaurantId || localStorage.getItem("prevRestaurantId");
  const [summaryServeTypes, setSummaryServeTypes] = useState(null);
  const DEFAULT_SERVE_TYPES = useMemo(() => ["DINE_IN", "TAKEAWAY"], []);
  const resolvedServeTypes = useMemo(() => {
    if (summaryServeTypes?.length) return summaryServeTypes;
    return DEFAULT_SERVE_TYPES;
  }, [summaryServeTypes, DEFAULT_SERVE_TYPES]);

  useEffect(() => {
    const fetchSupportedServeTypes = async () => {
      if (!positionGeohash || !activeRestaurantId) return;
      try {
        const response = await axios.get(
          `/api/catalogue/restaurant/${positionGeohash}/${activeRestaurantId}/summary`,
          {
            headers: {
              accept: "application/json",
            },
          }
        );
        const supported = response?.data?.supportedServeTypes;
        if (Array.isArray(supported) && supported.length > 0) {
          setSummaryServeTypes(supported);
          return;
        }
        setSummaryServeTypes(null);
      } catch (e) {
        console.log("Error fetching supported serve types:", e);
        setSummaryServeTypes(null);
      }
    };

    fetchSupportedServeTypes();
  }, [positionGeohash, activeRestaurantId]);

  useEffect(() => {
    if (!resolvedServeTypes.length) return;
    if (!orderType || !resolvedServeTypes.includes(orderType)) {
      setOrderType(resolvedServeTypes[0]);
    }
  }, [resolvedServeTypes, orderType]);
  useEffect(() => {
    const fetchCheckOut = async () => {
      // Check if restaurantId is available or fallback to localStorage/URL params
      const restaurantId = activeRestaurantId;

      if (!restaurantId) {
        console.log("Restaurant ID is missing");
        setLoading(false);
        return;
      }

      // Fetch current cart data from API to get add-on information
      let cartDishes = [];
      try {
        const cartResponse = await axios.get(`/api/nexus/cart/fetch/v3/${deviceId}`);
        if (cartResponse.data?.data?.items) {
          cartDishes = cartResponse.data.data.items;
        }
      } catch (error) {
        console.log("Error fetching cart data:", error);
        // Fallback to local cart data if API fails
        cartDishes = Object.entries(cartData).map(([dishId, data]) => {
          if (typeof data === 'number') {
            // Basic dish
            return {
              type: "BASIC",
              dishId: dishId,
              count: data
            };
          } else if (typeof data === 'object' && data !== null) {
            // Variant dish
            const variants = Object.entries(data).map(([variantId, count]) => ({
              variantId: variantId,
              count: count
            }));
            return {
              type: "VARIANT",
              dishId: dishId,
              variants: variants
            };
          }
          return null;
        }).filter(item => item !== null);
      }

      // No current location available (e.g. entered via /suggest-restaurant/:citySlug) —
      // checkout must use PREORDER_WITHOUT_LOCATION; that response won't include eta/etp.
      const hasCurrentLocation = Boolean(source);

      const reqData = hasCurrentLocation
        ? {
            userId: userId,
            restaurantId: restaurantId,
            deviceId: deviceId,
            cartDishes: cartDishes,
            tableId: tableId,
            sourceInfo: qrId
              ? {
                  type: "WEB",
                  qrId: qrId
                }
              : {
                  type: "WEB"
                },
            type: "WALK_IN"
          }
        : {
            userId: userId,
            restaurantId: restaurantId,
            deviceId: deviceId,
            cartDishes: cartDishes,
            sourceInfo: { type: "WEB" },
            type: "PREORDER_WITHOUT_LOCATION"
          };
      console.log("Request Data:", reqData);

      try {
        const response = await axios.post("/api/nexus/checkout/cart/v3", reqData, {
          headers: {
            accept: " application/json",
            "Content-Type": "application/json",
            "user-id": userId,
            "auth-token": accessToken,
          },
        });

        console.log("Checkout details", response.data);
        setCheckoutItems(response.data.data.paymentBreakup.items);
        // PREORDER_WITHOUT_LOCATION responses don't include readableEta/etp
        setTimeOfDelivery(response.data.data.readableEta || null);
        setPaymentBreakup(response.data.data.paymentBreakup);
        setAddress(response.data.data.restaurantAddress);
        setRestaurantName(response.data.data.restaurantName);
        setOrderId(response.data.data.orderId);
        setOrderType(response.data.data.serveType);
        setPayAtRestaurant(Boolean(response.data.data.payAtRestaurant));
        // If backend applies a default offer, surface it in UI just like manual apply
        if (response.data.data.offerContext) {
          setOfferData({
            data: {
              offerContext: response.data.data.offerContext,
              paymentBreakup: response.data.data.paymentBreakup,
            },
          });
        } else {
          setOfferData(null);
        }
      } catch (error) {
        console.log("Error fetching checkout details:", error);
      } finally {
        setLoading(false); // Set loading to false after data is fetched
      }
    };

    fetchCheckOut();
  }, []); // Empty dependency array ensures it only runs once on mount

  const submitServeType = async (type, metaInfo = null) => {
    const apiData = {
      type: "SERVE_TYPE",
      orderId: orderId,
      serveType: type,
      metaInfo: metaInfo,
    };

    try {
      const response = await axios.post("/api/nexus/order/update", apiData, {
        headers: {
          "Content-Type": "application/json",
          "auth-token": accessToken,
          " user-id": userId,
        },
      });
      console.log("Order type updated successfully", response);
      setOrderType(type);
    } catch (error) {
      console.log("Error updating order type:", error);
    }
  };

  const handleServeTypeChange = async (type) => {
    if (type === "RECEIVE_IN_BUS") {
      setBusDetailsError("");
      setShowBusDetailsModal(true);
      return;
    }
    await submitServeType(type, null);
  };

  const handleBusDetailsConfirm = async () => {
    const trimmedBusNumber = busNumber.trim();
    const trimmedSeatNumber = seatNumber.trim();
    if (!trimmedBusNumber || !trimmedSeatNumber) {
      setBusDetailsError("Please enter bus number and seat number.");
      return;
    }

    setBusDetailsError("");
    closeBusDetailsModal();
    await submitServeType("RECEIVE_IN_BUS", {
      BUS_NUMBER: trimmedBusNumber,
      SEAT_NUMBER: trimmedSeatNumber,
    });
  };

  const closeBusDetailsModal = () => {
    setShowBusDetailsModal(false);
    setBusDetailsError("");
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    // On iOS webviews, ensure viewport/scroll settles back after keyboard closes.
    requestAnimationFrame(() => {
      window.scrollBy(0, 0);
    });
  };

  // Remove Offer
  const handleRemoveOffer = async () => {
    console.log("Hello remove");
    try {
      const responce = await axios.post(
        `/api/nexus/checkout/benefit/remove/${orderId}`,
        {},
        {
          headers: {
            "user-id": userId,
            "auth-token": accessToken,
          },
        }
      );
      console.log(responce.data);
      console.log("wtf", responce.data.success);
      if (responce.data.success === true) {
        setOfferData(null);
        setPaymentBreakup(responce.data.data.paymentBreakup);
        console.log("remove money det", responce.data.data.paymentBreakup);
      }
    } catch (error) {
      console.log("Error in Remove Offer", error);
    }
  };
  console.log("offer data check", offerData, paymentBreakup);
  // offer benefit
  const fetchOffer = async () => {
    setOffersLoading(true); // Set loading to true when fetching offers
    const startTime = Date.now();

    try {
      console.log("O", orderId);
      if (orderId) {
        const response = await axios.get(
          `/api/payment/benefits/offers/applicable/${orderId}`,
          {
            headers: {
              "auth-token": accessToken,
              "user-id": userId,
            },
          }
        );
        console.log("Offer", response.data);
        console.log("title", response.data.data[0].amount);
        setOfferDetails(response.data.data);
      }
    } catch (error) {
      console.log("Error in fetching Offer", error);
    } finally {
      // Ensure loader is shown for at least 1 second
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, 1000 - elapsedTime);

      setTimeout(() => {
        setOffersLoading(false);
      }, remainingTime);
    }
  };

  // handle toggle
  function handleToggle() {
    setShowCheckout(true);
    fetchOffer();
  }

  // logic to handle payment
  const [isProcessing, setIsProcessing] = useState(false);

  const handlePayment = async () => {
    if (!timeOfDelivery && !etaOverrideSubmitted) {
      if (useDefaultTimeToArrive) {
        await submitEtaOverride(DEFAULT_TIME_TO_ARRIVE_MINUTES);
        return;
      }
      setEtaError("");
      setEtaAdjusted(false);
      setEtaConfirmWarned(false);
      setShakeEtaDialog(false);
      setEtaMinutes((prev) => (prev.trim() ? prev : String(ETA_DEFAULT_MINUTES)));
      setShowEtaModal(true);
      return;
    }
    await proceedToPayment();
  };

  const closeEtaModal = () => {
    setShowEtaModal(false);
    setEtaError("");
    setEtaAdjusted(false);
    setEtaConfirmWarned(false);
    setShakeEtaDialog(false);
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const ETA_STEP_MINUTES = 5;

  const stepEtaMinutes = (delta) => {
    setEtaError("");
    setEtaAdjusted(true);
    setEtaConfirmWarned(false);
    setEtaMinutes((prev) => {
      const current = Number(prev) || 0;
      const next = current + delta;
      return String(Math.max(ETA_STEP_MINUTES, next));
    });
  };

  const submitEtaOverride = async (overrideMinutes) => {
    const minutes = overrideMinutes ?? Number(etaMinutes);
    if (overrideMinutes === undefined && !etaMinutes.trim()) {
      setEtaError("Please enter a valid ETA in minutes.");
      return;
    }
    if (!Number.isFinite(minutes) || minutes <= 0) {
      setEtaError("Please enter a valid ETA in minutes.");
      return;
    }
    if (!orderId) {
      setEtaError("Missing order id. Please refresh and try again.");
      return;
    }

    setSubmittingEta(true);
    setEtaError("");
    try {
      await axios.post(
        "/api/nexus/order/update",
        {
          type: "ETA_OVERRIDES",
          orderId: orderId,
          eta: Date.now() + Math.round(minutes * 60 * 1000),
        },
        {
          headers: {
            "Content-Type": "application/json",
            "auth-token": accessToken,
            "user-id": userId,
          },
        }
      );
      setEtaOverrideSubmitted(true);
      closeEtaModal();
      await proceedToPayment();
    } catch (error) {
      console.log("Error submitting ETA override:", error);
      setEtaError("Failed to save your ETA. Please try again.");
    } finally {
      setSubmittingEta(false);
    }
  };

  const handleEtaConfirm = () => {
    // If the user hasn't touched the +/- steppers, nudge them to double-check
    // the value before we lock it in — shake once, then act on the next tap.
    if (!etaAdjusted && !etaConfirmWarned) {
      setEtaConfirmWarned(true);
      setShakeEtaDialog(true);
      setTimeout(() => setShakeEtaDialog(false), 500);
      return;
    }
    submitEtaOverride();
  };

  const proceedToPayment = async () => {
    setIsProcessing(true);
    setError(null);
    try {
      const response = await axios.post("/api/nexus/checkout/payment/init/v2", {
            orderId,
            userId,
            amount: paymentBreakup.total,
            payByLink: true
      },
      {
        headers: {
          "Content-Type": "application/json",
          "auth-token": accessToken,
          "user-id": userId,
        },
      });

      if (response.data.success) {
        const paymentData = response.data.data;
        const redirectionUrl =
          paymentData?.redirectionInfo?.redirectionUrl || paymentData?.redirectionUrl;
        const redirectionMessage =
          paymentData?.redirectionInfo?.redirectionMessage || paymentData?.redirectionMessage;

        // Check if payment is already marked as PAID (in-house payment)
        if (paymentData.paymentStatus === "PAID") {
          // Order confirmed => clear cart (best effort)
          try {
            if (deviceId) {
              await axios.post("/api/nexus/cart/clear/device/" + deviceId);
            }
          } catch (e) {
            // eslint-disable-next-line no-console
            console.log("Failed to clear cart:", e);
          } finally {
            try {
              setCartData({});
              setOfferData(null);
              setPaymentBreakup({});
            } catch (e) {
              // ignore
            }
          }
        }

        if (paymentData.paymentStatus === "PAID" && redirectionUrl) {

          if (payAtRestaurant) {
            if (!orderId) {
              setError("Missing order id. Please refresh and try again.");
              return;
            }
            setIsProcessing(true);
            try {
              if (redirectionUrl || redirectionMessage) {
                sessionStorage.setItem(
                  `orderRedirect:${orderId}`,
                  JSON.stringify({
                    redirectionUrl,
                    redirectionMessage,
                  })
                );
              }
            } catch (e) {
              // ignore storage errors
            }
            navigate(`/order-confirmation/${orderId}`, {
              replace: true,
              state: { redirectionUrl, redirectionMessage },
            });
            return;
          }

          // For in-house payments, redirect to the redirection URL (e.g., WhatsApp)
          window.location.href = redirectionUrl;
        } else if (paymentData.paymentUrl) {
          // For regular payments, navigate to payment page
          navigate('/payment', { state: { paymentUrl: paymentData.paymentUrl } });
        } else {
          setError("Invalid payment response");
        }
      } else {
        setError("Failed to generate payment link");
      }
    } catch (error) {
      console.error("Payment initiation failed:", error);
      setError("Failed to initiate payment");
    } finally {
      setIsProcessing(false);
    }
  };

  if (loading) {
    return (
      <div className="w-full h-screen flex justify-center items-center">
        <TailSpin
          height="80"
          width="80"
          color="#FBC02D"
          ariaLabel="loading"
          radius="1"
          visible={true}
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
        <div className="flex flex-col items-center gap-4 p-8 bg-white rounded-lg shadow-lg max-w-md w-full">
          <h2 className="text-2xl font-semibold text-red-600">Payment Error</h2>
          <p className="text-gray-600 text-center">{error}</p>
          <button 
            onClick={() => setError(null)}
            className="w-full bg-[#FBC02D] text-white text-center py-3 rounded-lg font-semibold hover:bg-[#e5ad28] transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return showCheckout ? (
    <div className="pt-24 flex flex-col w-full h-full items-center bg-[#FBFCFF]">
      <h2 className="text-[#171725] text-2xl md:text-3xl">Offers & Benefits</h2>

      <div className="pt-3 md:pt-5 pb-2 text-lg text-[#171725] px-2 md:px-5 lg:px-5 w-full h-full">
        <div className="flex flex-col">
          <h3 className="text-xl md:text-2xl">
            Exclusive offers and benefits for you
          </h3>

          {/* Close Button aligned to the right & compact size */}
          <button onClick={()=> setShowCheckout(false)} className="bg-red-600 text-white rounded px-3 py-1 self-end text-sm">
            Close
          </button>
        </div>

        <div className="mt-3">
          {offersLoading ? (
            <div className="w-full h-full flex justify-center items-center">
              <TailSpin
                height="40"
                width="40"
                color="#FBC02D"
                ariaLabel="loading"
                radius="1"
                visible={true}
              />
            </div>
          ) : (
            offerDetails.map((item, index) => (
              <OfferBenefitCard
                key={index}
                amount={item.amount}
                title={item.title}
                subtitle={item.subtitle}
                termsConditions={item.termsConditions}
                offerId={item.offerId}
                discount={item.amount}
              />
            ))
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="pt-16 flex flex-col w-full h-full items-center bg-[#FBFCFF] pb-32">
      <div className="flex flex-col w-full h-full md:w-full overflow-y-auto">
        <div className="mb-2 flex justify-center">
          <h1 className="text-2xl md:text-3xl font-semibold ">Checkout</h1>
        </div>

        {closingSoonLabel && (
          <div className="mx-2 md:mx-5 lg:mx-5 mb-4 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 flex items-center gap-3">
            <span className="flex-shrink-0 flex items-center justify-center w-10 h-10 rounded-full bg-amber-100 text-amber-600">
              <IoTimeOutline className="h-5 w-5" />
            </span>
            <div>
              <p className="font-semibold text-amber-800">
                {restaurantName || restaurantHours.name
                  ? `${restaurantName || restaurantHours.name} is`
                  : "This restaurant is"}{" "}
                closing soon
              </p>
              <p className="text-sm text-amber-700">
                {closingSoonLabel}
                {formatHourLabel(restaurantHours.closesAt) ? ` (at ${formatHourLabel(restaurantHours.closesAt)})` : ''}.
                {' '}You can still place this order, but please proceed with caution as it may not be fulfilled in time.
              </p>
            </div>
          </div>
        )}

        <div className="pt-3 md:pt-5 pb-2 text-lg text-[#171725] px-2 md:px-5 lg:px-5">
          <h2 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">
            Order Details
          </h2>
          {checkoutItems.map((item, index) => (
            <div key={index}>
              <div className="flex w-full justify-between font-semibold">
                <div className="flex justify-between">
                  <p>{item.dishName}</p>
                  <p className="text-[#FBC02D]">&nbsp;x{item.quantity}</p>
                </div>
                <p>₹{item.price / 100}</p>
              </div>
            </div>
          ))}
        </div>
        {/* Serve type selection */}
        <div className="px-2 md:px-5 lg:px-5 mt-3 w-full">
          <p className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">
            I want to get it served as
          </p>
          <div className="flex items-center justify-between bg-white rounded-xl border border-gray-200 p-2 gap-2 w-full">
            {resolvedServeTypes.map((serveType) => {
              const labelMap = {
                DINE_IN: "Dine in",
                TAKEAWAY: "Takeaway",
                RECEIVE_IN_BUS: "Receive in Bus",
              };
              const label =
                labelMap[serveType] ||
                serveType
                  .toLowerCase()
                  .split("_")
                  .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
                  .join(" ");
              const isSelected = orderType === serveType;
              return (
                <button
                  key={serveType}
                  className={`flex-1 min-w-0 px-3 py-3 rounded-lg text-sm font-semibold transition-colors ${
                    isSelected
                      ? "bg-[#FBC02D] text-white"
                      : "bg-transparent text-[#171725] hover:bg-gray-100"
                  }`}
                  onClick={() => handleServeTypeChange(serveType)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        </div>

        {(orderType === "RECEIVE_IN_BUS" || timeOfDelivery) && (
          <div className="flex items-center justify-center w-full h-14 bg-[#FFFFFF] mt-2">
            <h4 className="flex items-center text-sm">
              {orderType === "RECEIVE_IN_BUS" ? (
                <FaBus className="w-5 h-5 text-[#FBC02D]" />
              ) : (
                <FaCar className="w-5 h-5 text-[#FBC02D]" />
              )}
              {orderType === "RECEIVE_IN_BUS" ? (
                <p className="pl-2 font-semibold">
                  You will be served hot as your bus arrives
                </p>
              ) : (
                <>
                  <p className="pl-2 font-semibold"> Estimated time of arrival</p>
                  &nbsp;&nbsp;<b className="text-[#29BB89]"> {timeOfDelivery}</b>
                </>
              )}
            </h4>
          </div>
        )}

        <div className="flex flex-col gap-4 md:gap-6 px-2 md:px-5 lg:px-5">
          {/* Restaurant Address Section */}
          <div className="w-full">
            <h1 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">
              Restaurant Address
            </h1>
            <div className="bg-[#FFFFFF] pt-2 rounded-lg px-2 min-h-[80px]">
              <p className="font-semibold">{restaurantName}</p>
              <p className="">{address}</p>
            </div>
          </div>
        </div>

        <div className="mt-2 md:mt-2 pb-2 text-lg text-[#171725] px-2 md:px-5 lg:px-5 mb-1">
          <h1 className="text-xl md:text-2xl font-semibold text-[#171725] pb-3">
            Offers and Benefits
          </h1>
          <div className="flex bg-[#FFFFFF] h-auto rounded-lg items-center px-3 py-4 mt-2 ">
            <div className="w-[15%]">
              <TbShoppingBagDiscount className="h-8 w-8 text-[#FBC02D]" />
            </div>
            <div className="flex flex-col w-[85%]">
              {offerData ? (
                <div>
                  <p className="text-[#FBC02D] text-xl">
                    {offerData.data.offerContext.benefitMessage}
                  </p>
                  <p className="text-base">
                    {offerData.data.offerContext.couponCode} Coupon Applied
                  </p>
                </div>
              ) : (
                ""
              )}
              <div className="flex justify-between">
                {offerData ? (
                  <button
                    onClick={handleRemoveOffer}
                    className="text-[#eb3232] text-sm md:text-base"
                  >
                    Remove Offer
                  </button>
                ) : (
                  ""
                )}
                <button
                  onClick={handleToggle}
                  className="pl-4 text-lg font-semibold"
                >
                  {offerData ? "More Offer" : "View All Offers"}
                </button>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-2 md:mt-2 pb-2 text-lg text-[#171725] px-2 md:px-5 lg:px-5 ">
          <h1 className="text-xl md:text-2xl text-[#171725] pb-3 font-semibold">
            Price Breakdown
          </h1>
          <div className="flex justify-between pb-2">
            <p>Total item price</p>
            <p>₹{paymentBreakup.basePrice / 100}</p>
          </div>

          <div className="flex justify-between pb-2 text-green-600 font-semibold">
            <p>Discount</p>
            <p className="flex items-center gap-1">
              <FaMinus className="w-2" />₹
              {offerData
                ? paymentBreakup.discount / 100
                : paymentBreakup.discount / 100}
            </p>
          </div>

          <div className="flex justify-between pb-2">
            <p>Convenience fees</p>
            <p>₹{paymentBreakup.convenienceFee / 100}</p>
          </div>
          <div className="flex justify-between pb-2">
            <p>GST</p>
            <p>₹{paymentBreakup.gst / 100}</p>
          </div>
          <div className="flex justify-between font-bold">
            <p className="text-lg md:text-xl">
              {offerData ? "Net Payable:" : "Total"}
            </p>
            {offerData ? (
              <p>₹{offerData.data.paymentBreakup.total / 100}</p>
            ) : (
              <p>₹{paymentBreakup.total / 100}</p>
            )}
          </div>
        </div>

        <div className="px-4 md:px-5 lg:px-5 mt-1 mb-4">
          <p className="text-xs text-gray-400 text-center leading-relaxed">
            Tavahot and Travel Operator are only the service providers. All food items are
            prepared and supplied by independent restaurant partners. Tavahot and Travel
            Operator shall not be liable for any issues arising from the food or
            services provided by such partners.
          </p>
        </div>
      </div>
      {/* Fixed Pay Now button at bottom */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 z-10">
        <button
          onClick={handlePayment}
          className={`w-full md:w-[40%] bg-[#FBC02D] rounded-2xl py-3 text-lg font-semibold ${
            isProcessing ? "opacity-50 cursor-not-allowed" : ""
          }`}
          disabled={isProcessing}
        >
          {isProcessing ? "Processing..." : payAtRestaurant ? "Confirm & Pay Offline" : "Pay Now"}
        </button>
        {payAtRestaurant && (
          <p className="text-center text-sm text-[#171725] mt-2">
            Pay at counter
          </p>
        )}
      </div>

      {showBusDetailsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-lg w-full max-w-md p-6">
            <h3 className="text-lg font-semibold text-[#171725]">
              Enter bus details
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              We need your bus and seat number to serve you in the bus.
            </p>
            <div className="mt-4 space-y-3">
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Bus Number
                </label>
                <input
                  type="text"
                  value={busNumber}
                  onChange={(e) => setBusNumber(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#FBC02D]"
                  placeholder="e.g. HR26DF5210"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-gray-700">
                  Seat Number
                </label>
                <input
                  type="text"
                  value={seatNumber}
                  onChange={(e) => setSeatNumber(e.target.value)}
                  className="mt-1 w-full border border-gray-300 rounded-lg px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-[#FBC02D]"
                  placeholder="e.g. 32"
                />
              </div>
              {busDetailsError && (
                <p className="text-sm text-red-600">{busDetailsError}</p>
              )}
            </div>
            <div className="mt-5 flex justify-end gap-2">
              <button
                onClick={closeBusDetailsModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleBusDetailsConfirm}
                className="px-4 py-2 rounded-lg bg-[#FBC02D] text-white text-sm font-semibold"
              >
                Confirm
              </button>
            </div>
          </div>
        </div>
      )}

      {showEtaModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
          <div
            className={`bg-white rounded-lg w-full max-w-md p-6 ${
              shakeEtaDialog ? "shake-dialog" : ""
            }`}
          >
            <h3 className="text-lg font-semibold text-[#171725]">
              In how much time are you reaching the restaurant?
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Take 5 minutes extra than your Google Maps time to deal with
              uncertainty. If Google Maps is showing 12 minutes, put 17 here.
            </p>
            <div className="mt-5">
              <label className="block text-center text-sm font-medium text-gray-700">
                Estimated time to reach
              </label>
              <div className="mt-3 flex items-center justify-center gap-5">
                <button
                  type="button"
                  onClick={() => stepEtaMinutes(-ETA_STEP_MINUTES)}
                  aria-label={`Decrease by ${ETA_STEP_MINUTES} minutes`}
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-300 text-[#171725] hover:bg-gray-100 active:bg-gray-200"
                >
                  <FaMinus className="w-3 h-3" />
                </button>
                <div className="text-center min-w-[88px]">
                  <span className="text-3xl font-bold text-[#171725]">
                    {etaMinutes || 0}
                  </span>
                  <p className="text-xs text-gray-500 mt-0.5">minutes</p>
                </div>
                <button
                  type="button"
                  onClick={() => stepEtaMinutes(ETA_STEP_MINUTES)}
                  aria-label={`Increase by ${ETA_STEP_MINUTES} minutes`}
                  className="w-11 h-11 flex items-center justify-center rounded-full border border-gray-300 text-[#171725] hover:bg-gray-100 active:bg-gray-200"
                >
                  <FaPlus className="w-3 h-3" />
                </button>
              </div>
              {etaError && (
                <p className="text-sm text-red-600 text-center mt-3">{etaError}</p>
              )}
            </div>
            <div className="mt-6 flex justify-end gap-2">
              <button
                onClick={closeEtaModal}
                className="px-4 py-2 rounded-lg border border-gray-300 text-sm font-semibold text-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={handleEtaConfirm}
                disabled={submittingEta}
                className={`px-4 py-2 rounded-lg bg-[#FBC02D] text-white text-sm font-semibold ${
                  submittingEta ? "opacity-50 cursor-not-allowed" : ""
                }`}
              >
                {submittingEta ? "Saving..." : "Confirm & Pay"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Checkout;
